import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { WalletAddress } from '@root/entities/wallet/model/WalletAddress'
import { useTranslation } from '@root/shared/localization'
import { Dialog, Input } from '@root/shared/ui-kit'
import { InputChips } from '@root/shared/ui-kit/inputs/input-chip'

import { reatomAddWalletTracker } from '../../atoms/reatom-add-wallet-tracker'

import { AddWalletTrackerDialogEmoji } from './add-wallet-tracker-dialog-emoji'

import type { DialogControlProps } from '@root/shared/ui-kit'

type AddWalletTrackerDialogProps = {} & DialogControlProps

type Values = {
  name: string
  address: string
  tags: Array<string> | undefined
  emoji: string
}

export const AddWalletTrackerDialog: React.FC<AddWalletTrackerDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()

  const modalScreenRef = useRef<HTMLDivElement>(null)
  const [emojiOpen, setEmojiOpen] = useState<boolean>(false)
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomAddWalletTracker(), [])
  const [state] = useAtom(model)
  const addWallet = useAction(model.execute)
  const [selectedNetwork] = useAtom(selectedNetworkAtom)

  const { control, handleSubmit, formState, reset, watch, setValue } =
    useForm<Values>({
      defaultValues: {
        name: '',
        address: '',
        tags: undefined,
        emoji: '',
      },
      mode: 'all',
    })

  const emoji = watch('emoji')

  const handleSubmitInternal = handleSubmit((data) => {
    addWallet({
      address: WalletAddress(data.address),
      name: data.name,
      tags: data.tags ?? [],
      emoji: data.emoji.trim() || '😀',
    })
  })

  useEffect(() => {
    if (AsyncStateN.isSuccess(state)) {
      onOpenChange?.(false)
      reset()
    }
  }, [state, onOpenChange, reset])

  const toggleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    reset()
  }

  return (
    <Root
      open={open}
      onOpenChange={toggleOpenChange}
      describedBy={t('wallet-tracker.add-wallet.title')}
    >
      <Dialog.Header>{t('wallet-tracker.add-wallet.title')}</Dialog.Header>
      <Content ref={modalScreenRef}>
        <Fields onSubmit={handleSubmitInternal}>
          <Controller
            control={control}
            name="name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                startContent={
                  <AddWalletTrackerDialogEmoji
                    emoji={emoji}
                    open={emojiOpen}
                    onOpenChange={setEmojiOpen}
                    onSetEmoji={(emoji) => setValue('emoji', emoji)}
                    container={modalScreenRef.current ?? undefined}
                  />
                }
                size="h32"
                placeholder={t('wallet-tracker.wallet-name.placeholder')}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="address"
            rules={{
              required: true,
              minLength: 3,
              validate: (value) => {
                if (!Network.validateAddress(selectedNetwork)(value)) {
                  return t('validation.invalid_address')
                }

                return undefined
              },
            }}
            render={({ field, fieldState }) => (
              <Input
                size="h32"
                placeholder={t('wallet-tracker.wallet-address.placeholder')}
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="tags"
            rules={{ required: false }}
            render={({ field }) => (
              <InputChips
                {...field}
                addOnBlur
                value={field.value}
                placeholder={t('wallet-tracker.wallet-tags.placeholder')}
                unique
                variant="outlined"
                size="small"
              />
            )}
          />
          <Button
            css={css`
              justify-self: center;
            `}
            radius="rounded"
            size="xs"
            variant="soft"
            type="submit"
            busy={state.loading}
            disabled={!formState.isValid}
          >
            {t('wallet-tracker.add-wallet.title')}
          </Button>
        </Fields>
      </Content>
    </Root>
  )
}

const Fields = styled.form`
  display: grid;
  padding-block: 18px 24px;
  padding-inline: 10px;
  gap: 12px;
`

const Root = styled(Dialog)`
  width: 390px;
`

const Content = styled.div``
